import React, { useEffect, useRef, useState } from "react";
import type { AvatarState, SpeechState } from "../../store/types";
import "./Avatar.css";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Button } from "@mui/material";
import { SoundController } from "../DiagnosisComponents/SoundController";
import { getAvatarService } from "../../services/avatar";

interface AvatarSectionProps {
  sessionDetails?: any;
  avatarState: AvatarState;
  speechState: SpeechState;
}

export const AvatarSection: React.FC<AvatarSectionProps> = ({
  sessionDetails,
  avatarState,
  speechState,
}) => {
  const service = getAvatarService(avatarState.provider);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [streamAttached, setStreamAttached] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(() => service.isPlaybackBlocked());
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const isVideoVisible = avatarState.isConnected && (avatarState.streamActive || streamAttached);

  // Debug logs
  useEffect(() => {
    console.log("AvatarSection render:", {
      hasSessionDetails: !!sessionDetails,
      sessionId: sessionDetails?.session?.sessionId,
      caseTitle: sessionDetails?.case?.title,
      avatarEnabled: avatarState.isEnabled,
      avatarConnected: avatarState.isConnected,
      avatarConnecting: avatarState.isConnecting,
      speechEnabled: speechState.isEnabled,
      speechListening: speechState.isListening,
        isDisconnecting,
        streamActive: avatarState.streamActive,
        streamAttached,
      });
  }, [sessionDetails, avatarState, speechState, isDisconnecting, streamAttached]);

  /**
   * Reset disconnecting state when avatar state changes
   */
  useEffect(() => {
    if (!avatarState.isConnected && !avatarState.isConnecting) {
      // Reset disconnecting state when avatar is fully disconnected
      setIsDisconnecting(false);
      setPlaybackBlocked(service.isPlaybackBlocked());
    }
  }, [avatarState.isConnected, avatarState.isConnecting, service]);

  /**
   * Reflect connection events in the video panel.
   */
  useEffect(() => {
    const handleStreamReady = () => {
      console.log("Avatar stream ready event received");
      setStreamAttached(true);
      setIsDisconnecting(false); // Reset on successful connection
    };

    const handleDisconnected = () => {
      console.log("Avatar disconnected event received");
      setStreamAttached(false);
      setPlaybackBlocked(false);
      // Clear video when stream is removed
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setIsDisconnecting(false); // Reset on disconnect event
    };

    const handlePlaybackBlocked = (event: Event) => {
      setPlaybackBlocked((event as CustomEvent<boolean>).detail);
    };
    window.addEventListener("avatar-playback-blocked", handlePlaybackBlocked);
    window.addEventListener("avatar-stream-ready", handleStreamReady);
    window.addEventListener("avatar-disconnected", handleDisconnected);

    return () => {
      window.removeEventListener("avatar-playback-blocked", handlePlaybackBlocked);
      window.removeEventListener("avatar-stream-ready", handleStreamReady);
      window.removeEventListener("avatar-disconnected", handleDisconnected);
    };
  }, []);

  // The SDK needs a mounted video target before starting the connection.
  useEffect(() => {
    const element = videoRef.current;
    if (element) service.attachToElement(element);
    // The page cleanup hook owns the session. Responsive panel remounts only
    // detach the old media element and attach the same stream to its replacement.
    return () => { if (element) service.detachFromElement(element); };
  }, [service]);

  /**
   * Render avatar status
   */
  const renderStatus = () => {
    if (!avatarState.isEnabled) {
      return "Avatar streaming not available";
    }

    if (playbackBlocked) {
      return "Click Play to start avatar audio and video";
    }

    if (avatarState.error) {
      return `Error: ${avatarState.error}`;
    }

    if (isDisconnecting) {
      return "Disconnecting...";
    }

    if (avatarState.isConnected) {
      return avatarState.isSpeaking ? "Speaking..." : "Ready";
    }

    return sessionDetails
      ? "Avatar ready to connect"
      : "Select a case to enable avatar";
  };

  return (
    <div className="avatar-section">
      {/* Video element for avatar stream */}
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        <video
          id="patient-avatar-video"
          ref={videoRef}
          className="avatar-video"
          autoPlay
          playsInline
          style={isVideoVisible ? {} : { display: "none" }}
        />
        {isVideoVisible && <SoundController />}
      </Box>

      {playbackBlocked && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
          <Button variant="contained" onClick={() => { void service.resumePlayback(); }}>
            Play avatar audio and video
          </Button>
        </Box>
      )}

      {/* Placeholder when no avatar */}
      {!isVideoVisible && !avatarState.isConnecting && (
        <div className="avatar-placeholder">
          {sessionDetails && avatarState.isEnabled
            ? 'Click "Speak now" to begin the live interview'
            : !avatarState.isEnabled
              ? "Avatar streaming is not configured"
              : "Select a case to enable avatar"}
        </div>
      )}

      {/* Status indicator */}
      {avatarState.isConnecting && !playbackBlocked ? (
        <Box sx={{ justifyContent: "center", alignItems: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <div
          className="avatar-status"
          style={{
            display:
              avatarState.isConnected ||
              avatarState.error ||
              avatarState.isConnecting ||
              isDisconnecting
                ? "block"
                : "none",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {renderStatus()}
        </div>
      )}
    </div>
  );
};
